import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { orgAdminService } from "../../features/org-admin/service";

/** Landing page for the superadmin-issued "copy org-admin link"
 *  (/org-admin/magic-login?token=...). Public route — the token itself is
 *  the credential, redeemed once against POST /api/org-admin/magic-login. */
const OrgAdminMagicLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setError("This link is missing its token.");
      return;
    }

    (async () => {
      try {
        await orgAdminService.magicLogin(token);
        navigate("/org-admin/dashboard", { replace: true });
      } catch (err: any) {
        setError(err?.response?.data?.detail || "This organization admin link is no longer valid.");
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {error ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 mb-4">
              <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold text-white mb-2">Link not valid</h1>
            <p className="text-sm text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate("/org-admin/login", { replace: true })}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors"
            >
              Go to sign in
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 text-gray-400">
            <svg className="w-8 h-8 animate-spin text-indigo-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-sm">Signing you into the org-admin console…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgAdminMagicLoginPage;
